import { useEffect, useState, useCallback } from "react";
import { PageTitle } from "@/components/PageHeader";
import { motion, AnimatePresence } from "framer-motion";
import { useCachedState } from "@/lib/swr";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { AllowanceDefaults } from "@/components/AllowanceDefaults";
import { Layers, DollarSign, TrendingUp, Save, RefreshCw, Pencil, Check, X, ChevronRight, Search, Info } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function priceLabel(a) {
  const bc = a.billing_class || (a.billable ? "addon" : "free");
  if (bc === "base") return "In base plan";
  if (bc === "free") return "Free";
  if (a.pricing === "tiered") {
    const lo = Math.min(...a.tiers.map((t) => t.price));
    const hi = Math.max(...a.tiers.map((t) => t.price));
    return `$${lo}–$${hi}/mo`;
  }
  return `$${a.price}/mo`;
}

const marginColor = (m) => (m > 0 ? "text-emerald-600" : m < 0 ? "text-redline-600" : "text-muted-foreground");

function Metric({ label, value, className = "" }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">{label}</div>
      <div className={`font-mono font-semibold text-sm mt-0.5 tabular-nums ${className}`}>{value}</div>
    </div>
  );
}

function CommunityRow({ c, catalog, basePlans, open, onToggle, onToggleEnt, onSaveUnits, onSaveBase }) {
  const activeCount = catalog.filter((a) => a.billable && c.entitlements[a.key]).length;
  const plan = basePlans.find((p) => p.key === c.base_plan);
  const planPrice = plan ? Number(plan.base_price || 0) : 0;
  return (
    <div className="border-b border-sand-200 last:border-0">
      <button
        data-testid={`plan-row-${c.tenant_id}`}
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-sand-50 transition-colors"
      >
        <ChevronRight className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-90" : ""}`} strokeWidth={2} />
        <div className="min-w-0 flex-1">
          <div className="font-heading font-bold text-ink truncate">{c.name}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            <span className={`font-semibold ${c.base_plan ? "text-ink/70" : "text-amber-600"}`}>{plan ? plan.name : "No base plan"}</span>
            <span className="mx-1.5 text-sand-300">·</span>
            <span className="font-mono tabular-nums">{c.unit_count}</span> units
            <span className="mx-1.5 text-sand-300">·</span>
            <span className={activeCount ? "text-ink/70 font-medium" : "text-muted-foreground"}>{activeCount} paid add-on{activeCount === 1 ? "" : "s"}</span>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-6 shrink-0">
          <Metric label="MRR" value={money(c.revenue)} className="text-emerald-600" />
          <Metric label="Cost" value={money(c.usage_cost)} className="text-ink/50" />
          <Metric label="Margin" value={money(c.margin)} className={marginColor(c.margin)} />
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="bg-sand-50 px-5 py-5 border-t border-sand-200">
              {/* mobile metrics */}
              <div className="sm:hidden grid grid-cols-3 gap-2 mb-4 rounded-xl bg-white border border-sand-200 p-3">
                <Metric label="MRR" value={money(c.revenue)} className="text-emerald-600" />
                <Metric label="Cost" value={money(c.usage_cost)} className="text-ink/50" />
                <Metric label="Margin" value={money(c.margin)} className={marginColor(c.margin)} />
              </div>

              {/* MRR breakdown: base plan + add-ons */}
              <div className="rounded-xl bg-white border border-sand-200 p-3 mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mr-1">MRR</span>
                <span className="font-mono tabular-nums" data-testid={`mrr-base-${c.tenant_id}`}>{money(c.base_revenue)}<span className="text-xs text-muted-foreground"> base</span></span>
                <span className="text-sand-300">+</span>
                <span className="font-mono tabular-nums" data-testid={`mrr-addon-${c.tenant_id}`}>{money(c.addon_revenue)}<span className="text-xs text-muted-foreground"> add-ons</span></span>
                <span className="text-sand-300">=</span>
                <span className="font-mono tabular-nums font-bold text-emerald-600" data-testid={`mrr-total-${c.tenant_id}`}>{money(c.revenue)}</span>
              </div>

              {/* Base plan + base price */}
              <div className="rounded-xl bg-white border border-sand-200 p-4 mb-4">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">Base subscription</div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-muted-foreground">Plan</label>
                    <select data-testid={`base-plan-select-${c.tenant_id}`} value={c.base_plan || ""}
                      onChange={(e) => onSaveBase(c.tenant_id, { base_plan: e.target.value || null })}
                      className="rounded-lg bg-sand-50 border border-sand-200 px-3 py-1.5 text-sm outline-none focus:border-ink">
                      <option value="">— none —</option>
                      {basePlans.map((p) => <option key={p.key} value={p.key}>{`${p.name} ($${p.base_price || 0}/mo)`}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-muted-foreground">Price override</label>
                    <span className="text-muted-foreground text-sm">$</span>
                    <input data-testid={`base-price-input-${c.tenant_id}`} type="number" min="0" step="1"
                      defaultValue={c.base_price ?? ""} key={`bp-${c.tenant_id}-${c.base_price ?? "def"}`}
                      placeholder={planPrice ? String(planPrice) : "0"}
                      onBlur={(e) => onSaveBase(c.tenant_id, { base_price: e.target.value === "" ? null : e.target.value })}
                      className="w-24 rounded-lg bg-sand-50 border border-sand-200 px-2 py-1.5 text-sm font-mono tabular-nums outline-none focus:border-ink" />
                    <span className="text-xs text-muted-foreground">/mo {c.base_price == null ? "· using plan price" : "· custom"}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2 text-xs text-redline-700 bg-redline-50 px-3 py-2.5 rounded-xl border border-redline-100 mb-4">
                <Info className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={2} />
                <span>MRR = base plan price + enabled paid add-ons. Toggling an add-on subscribes this community to it; add-ons with a live capability (<span className="font-semibold">Voice Concierge, WhatsApp, Portfolio Intelligence</span>) switch on/off in the product instantly.</span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Units</label>
                <input
                  data-testid={`units-input-${c.tenant_id}`}
                  type="number" min="0" step="1" defaultValue={c.unit_count}
                  onBlur={(e) => onSaveUnits(c.tenant_id, e.target.value)}
                  className="w-24 rounded-lg bg-white border border-sand-200 px-3 py-1.5 text-sm font-mono tabular-nums outline-none focus:border-ink"
                />
                <span className="text-xs text-muted-foreground">drives per-door &amp; tiered add-on pricing</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {catalog.map((a) => {
                  const on = !!c.entitlements[a.key];
                  const bc = a.billing_class || (a.billable ? "addon" : "free");
                  return (
                    <div key={a.key}
                      className={`rounded-xl border bg-white p-4 transition-colors ${a.billable && on ? "border-redline-300 ring-1 ring-redline-100" : "border-sand-200"}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-heading font-bold text-sm text-ink">{a.name}</div>
                          <div className="text-xs font-mono text-ink/60 mt-0.5">{priceLabel(a)}</div>
                        </div>
                        {bc === "addon" ? (
                          <Switch data-testid={`ent-${a.key}-${c.tenant_id}`} checked={on}
                            onCheckedChange={(v) => onToggleEnt(c.tenant_id, a.key, v)} />
                        ) : bc === "base" ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-ink bg-sand-100 border border-sand-200 px-2 py-1 rounded-full shrink-0">In base</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full shrink-0"><Check className="h-3 w-3" strokeWidth={2.5} />Free</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{a.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Plans() {
  const [data, setData] = useCachedState("plans:cur", null);
  const [month, setMonth] = useState("");
  const [q, setQ] = useState("");
  const [openRows, setOpenRows] = useState({});
  const [editCatalog, setEditCatalog] = useState(false);
  const [draft, setDraft] = useState([]);
  const [editPlans, setEditPlans] = useState(false);
  const [planDraft, setPlanDraft] = useState([]);

  const load = useCallback(async (m) => {
    const qs = m ? `?month=${m}` : "";
    const r = await api.get(`/admin/plans${qs}`);
    setData(r.data);
    setMonth(r.data.month);
    setDraft(JSON.parse(JSON.stringify(r.data.catalog)));
    setPlanDraft(JSON.parse(JSON.stringify(r.data.base_plans || [])));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleEntitlement = async (tid, key, val) => {
    setData((cur) => ({
      ...cur,
      communities: cur.communities.map((c) => c.tenant_id === tid ? { ...c, entitlements: { ...c.entitlements, [key]: val } } : c),
    }));
    try {
      await api.put(`/tenants/${tid}/entitlements`, { entitlements: { [key]: val } });
      toast.success(val ? "Add-on provisioned" : "Add-on removed");
      load(month);
    } catch { toast.error("Could not update"); load(month); }
  };

  const saveUnits = async (tid, units) => {
    try {
      await api.put(`/tenants/${tid}/entitlements`, { unit_count: parseInt(units) || 0 });
      toast.success("Unit count saved");
      load(month);
    } catch { toast.error("Could not save units"); }
  };

  const saveBase = async (tid, patch) => {
    setData((cur) => ({
      ...cur,
      communities: cur.communities.map((c) => c.tenant_id === tid ? { ...c, ...patch } : c),
    }));
    try {
      await api.put(`/tenants/${tid}/entitlements`, patch);
      toast.success("Base plan saved");
      load(month);
    } catch { toast.error("Could not save base plan"); load(month); }
  };

  const setDraftClass = (key, value) => {
    setDraft((cur) => cur.map((a) => a.key === key ? { ...a, billing_class: value, billable: value === "addon" } : a));
  };

  const setDraftPrice = (key, field, value, tierIdx = null) => {
    setDraft((cur) => cur.map((a) => {
      if (a.key !== key) return a;
      if (tierIdx != null) return { ...a, tiers: a.tiers.map((t, i) => i === tierIdx ? { ...t, price: value } : t) };
      return { ...a, [field]: value };
    }));
  };

  const saveCatalog = async () => {
    const catalog = draft.map((a) => ({
      ...a,
      price: a.pricing === "flat" ? (parseFloat(a.price) || 0) : a.price,
      tiers: a.pricing === "tiered" ? a.tiers.map((t) => ({ ...t, price: parseFloat(t.price) || 0 })) : a.tiers,
    }));
    try {
      await api.put("/admin/plans/catalog", { catalog });
      toast.success("Prices saved");
      setEditCatalog(false);
      load(month);
    } catch { toast.error("Could not save prices"); }
  };

  const setPlanField = (key, field, value) => {
    setPlanDraft((cur) => cur.map((p) => p.key === key ? { ...p, [field]: field === "features" ? value.split("\n") : value } : p));
  };

  const saveBasePlans = async () => {
    const base_plans = planDraft.map((p) => ({ ...p, base_price: Number(p.base_price) || 0, features: (p.features || []).filter(Boolean) }));
    try {
      await api.put("/admin/plans/base-plans", { base_plans });
      toast.success("Base plans saved");
      setEditPlans(false);
      load(month);
    } catch { toast.error("Could not save base plans"); }
  };

  if (!data) {
    return <div className="flex items-center justify-center h-64"><div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>;
  }

  const catalog = data.catalog;
  const communities = data.communities.filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="space-y-8" data-testid="plans-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <PageTitle title={<><Layers className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Plans &amp; Add-ons</>} about="Your per-community billing control center — subscribe each community to premium add-ons, set list prices, and track margin (list-price revenue minus real usage cost)." />
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="plans-month-select" value={month} onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}
            className="rounded-full bg-white border border-sand-200 px-4 py-2 text-sm font-medium font-mono outline-none focus:border-ink">
            {data.months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <button data-testid="plans-refresh" onClick={() => load(month)} className="h-9 w-9 rounded-full bg-white border border-sand-200 flex items-center justify-center hover:border-ink transition-colors">
            <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>
      <AllowanceDefaults />

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: DollarSign, label: "Total MRR", val: data.total_revenue, sub: `Base ${money(data.total_base_revenue)} + add-ons ${money(data.total_addon_revenue)} · ${month}`, cls: "text-emerald-600" },
          { icon: DollarSign, label: "Usage cost", val: data.total_cost, sub: "AI tokens + SMS + voice this month", cls: "text-ink" },
          { icon: TrendingUp, label: "Gross margin", val: data.total_margin, sub: "Revenue minus usage cost", cls: marginColor(data.total_margin) },
        ].map((k, i) => (
          <div key={i} className="bg-white rounded-2xl border border-sand-200 p-5">
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><k.icon className="h-4 w-4" /> {k.label}</div>
            <div data-testid={i === 0 ? "plans-total-revenue" : i === 1 ? "plans-total-cost" : "plans-total-margin"}
              className={`font-mono text-3xl font-bold mt-2 tabular-nums ${k.cls}`}>{money(k.val)}</div>
            <div className="text-xs text-muted-foreground mt-1">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Entitlements by community */}
      <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-sand-200 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-heading font-bold text-lg">Entitlements by community</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Expand a community to subscribe it to add-ons. Each row shows that community's MRR, cost and margin for {month}.</p>
          </div>
          <div className="relative">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="plans-community-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search communities…"
              className="rounded-full bg-sand-50 border border-sand-200 pl-9 pr-4 py-2 text-sm outline-none focus:border-ink w-56" />
          </div>
        </div>
        {communities.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground">No communities match “{q}”.</div>
        ) : (
          communities.map((c) => (
            <CommunityRow key={c.tenant_id} c={c} catalog={catalog} basePlans={data.base_plans || []}
              open={!!openRows[c.tenant_id]}
              onToggle={() => setOpenRows((o) => ({ ...o, [c.tenant_id]: !o[c.tenant_id] }))}
              onToggleEnt={toggleEntitlement} onSaveUnits={saveUnits} onSaveBase={saveBase} />
          ))
        )}
      </div>

      {/* Base plans */}
      <div className="bg-white rounded-2xl border border-sand-200 p-5" data-testid="base-plans-card">
        <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
          <h2 className="font-heading font-bold text-lg">Base plans <span className="text-xs font-normal text-muted-foreground">· shown on the public site</span></h2>
          {editPlans ? (
            <div className="flex items-center gap-2">
              <button data-testid="plans-cancel" onClick={() => { setEditPlans(false); setPlanDraft(JSON.parse(JSON.stringify(data.base_plans || []))); }}
                className="flex items-center gap-1.5 rounded-full bg-sand-100 text-ink font-semibold px-4 py-2 text-sm hover:bg-sand-200 transition-colors"><X className="h-4 w-4" /> Cancel</button>
              <button data-testid="plans-save" onClick={saveBasePlans}
                className="flex items-center gap-1.5 rounded-full bg-ink text-white font-semibold px-4 py-2 text-sm hover:bg-ink-700 transition-colors"><Save className="h-4 w-4" /> Save plans</button>
            </div>
          ) : (
            <button data-testid="plans-edit" onClick={() => setEditPlans(true)}
              className="flex items-center gap-1.5 rounded-full bg-white border border-sand-200 text-ink font-semibold px-4 py-2 text-sm hover:border-ink transition-colors"><Pencil className="h-4 w-4" /> Edit plans</button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-4">Read live by the marketing site and the demo assistant — edit here and they update everywhere, no redeploy.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {(editPlans ? planDraft : (data.base_plans || [])).map((p) => (
            <div key={p.key} data-testid={`base-plan-${p.key}`} className={`rounded-xl border p-4 ${p.popular ? "border-ink" : "border-sand-200"}`}>
              {!editPlans ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="font-heading font-bold text-sm">{p.name}</div>
                    {p.popular && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-redline-50 text-redline-700">POPULAR</span>}
                  </div>
                  <div className="mt-1 font-mono text-2xl font-bold">{p.price}<span className="text-xs text-muted-foreground font-normal font-sans">{p.contact ? "" : "/mo"}</span></div>
                  <div className="text-xs text-muted-foreground mt-0.5">{p.units}</div>
                  <div className="text-[10px] text-emerald-700 font-mono mt-0.5" data-testid={`base-plan-mrr-${p.key}`}>MRR basis: ${p.base_price || 0}/mo</div>
                  <ul className="mt-2 space-y-1">
                    {(p.features || []).slice(0, 6).map((f) => <li key={f} className="text-xs text-ink-700 flex gap-1.5"><Check className="h-3 w-3 mt-0.5 shrink-0 text-redline-500" />{f}</li>)}
                  </ul>
                </>
              ) : (
                <div className="space-y-2">
                  <input data-testid={`plan-name-${p.key}`} value={p.name} onChange={(e) => setPlanField(p.key, "name", e.target.value)}
                    className="w-full rounded-lg bg-sand-50 border border-sand-200 px-2 py-1.5 text-sm font-bold outline-none focus:border-ink" placeholder="Name" />
                  <input data-testid={`plan-price-${p.key}`} value={p.price} onChange={(e) => setPlanField(p.key, "price", e.target.value)}
                    className="w-full rounded-lg bg-sand-50 border border-sand-200 px-2 py-1.5 text-sm font-mono outline-none focus:border-ink" placeholder="$199 (display)" />
                  <div className="flex items-center gap-1.5">
                    <span className="text-muted-foreground text-xs">MRR $</span>
                    <input data-testid={`plan-baseprice-${p.key}`} type="number" min="0" step="1" value={p.base_price ?? ""} onChange={(e) => setPlanField(p.key, "base_price", e.target.value)}
                      className="w-full rounded-lg bg-sand-50 border border-sand-200 px-2 py-1.5 text-sm font-mono outline-none focus:border-ink" placeholder="199 (used for MRR)" />
                  </div>
                  <input data-testid={`plan-units-${p.key}`} value={p.units} onChange={(e) => setPlanField(p.key, "units", e.target.value)}
                    className="w-full rounded-lg bg-sand-50 border border-sand-200 px-2 py-1.5 text-xs outline-none focus:border-ink" placeholder="Units" />
                  <textarea data-testid={`plan-features-${p.key}`} value={(p.features || []).join("\n")} onChange={(e) => setPlanField(p.key, "features", e.target.value)}
                    rows={5} className="w-full rounded-lg bg-sand-50 border border-sand-200 px-2 py-1.5 text-xs outline-none focus:border-ink" placeholder="One feature per line" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Add-on catalog / list prices */}
      <div className="bg-white rounded-2xl border border-sand-200 p-5">
        <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
          <h2 className="font-heading font-bold text-lg">Add-on catalog &amp; list prices</h2>
          {editCatalog ? (
            <div className="flex items-center gap-2">
              <button data-testid="catalog-cancel" onClick={() => { setEditCatalog(false); setDraft(JSON.parse(JSON.stringify(catalog))); }}
                className="flex items-center gap-1.5 rounded-full bg-sand-100 text-ink font-semibold px-4 py-2 text-sm hover:bg-sand-200 transition-colors"><X className="h-4 w-4" /> Cancel</button>
              <button data-testid="catalog-save" onClick={saveCatalog}
                className="flex items-center gap-1.5 rounded-full bg-ink text-white font-semibold px-4 py-2 text-sm hover:bg-ink-700 transition-colors"><Save className="h-4 w-4" /> Save prices</button>
            </div>
          ) : (
            <button data-testid="catalog-edit" onClick={() => setEditCatalog(true)}
              className="flex items-center gap-1.5 rounded-full bg-white border border-sand-200 text-ink font-semibold px-4 py-2 text-sm hover:border-ink transition-colors"><Pencil className="h-4 w-4" /> Edit prices</button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-4">The single source of truth for add-on pricing. Editing a price here changes the MRR math for every community it's enabled on.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(editCatalog ? draft : catalog).map((a) => (
            <div key={a.key} data-testid={`catalog-card-${a.key}`} className="rounded-xl border border-sand-200 p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="font-heading font-bold text-sm">{a.name}</div>
                {(() => {
                  const bc = a.billing_class || (a.billable ? "addon" : "free");
                  if (bc === "base") return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sand-100 text-ink border border-sand-200">IN BASE</span>;
                  if (bc === "free") return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">FREE</span>;
                  return null;
                })()}
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{a.desc}</p>
              {editCatalog && (
                <div className="flex gap-1 mt-3" data-testid={`class-select-${a.key}`}>
                  {[["base", "In base"], ["addon", "Add-on"], ["free", "Free"]].map(([val, lbl]) => {
                    const cur = a.billing_class || (a.billable ? "addon" : "free");
                    return (
                      <button key={val} type="button" data-testid={`class-${a.key}-${val}`} onClick={() => setDraftClass(a.key, val)}
                        className={`text-[10px] font-bold px-2.5 py-1 rounded-full border transition-colors ${cur === val ? "bg-ink text-white border-ink" : "bg-white text-ink/60 border-sand-200 hover:border-ink/40"}`}>{lbl}</button>
                    );
                  })}
                </div>
              )}
              <div className="mt-3">
                {!editCatalog ? (
                  <span className="text-sm font-bold font-mono text-ink">{priceLabel(a)}</span>
                ) : (a.billing_class || (a.billable ? "addon" : "free")) !== "addon" ? (
                  <span className="text-xs text-muted-foreground">{(a.billing_class || (a.billable ? "addon" : "free")) === "base" ? "Included in the base plan — no separate charge" : "Free for everyone — never billed"}</span>
                ) : a.pricing === "flat" ? (
                  <label className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">$</span>
                    <input data-testid={`price-input-${a.key}`} type="number" min="0" step="1" value={a.price} disabled={!a.billable}
                      onChange={(e) => setDraftPrice(a.key, "price", e.target.value)}
                      className="w-24 rounded-lg bg-sand-50 border border-sand-200 px-2 py-1.5 font-mono outline-none focus:border-ink disabled:opacity-50" />
                    <span className="text-muted-foreground text-xs">/mo</span>
                  </label>
                ) : (
                  <div className="space-y-1.5">
                    {a.tiers.map((tier, i) => (
                      <label key={i} className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground w-24">{tier.max_units == null ? "300+ units" : `≤ ${tier.max_units} units`}</span>
                        <span className="text-muted-foreground">$</span>
                        <input data-testid={`price-input-${a.key}-${i}`} type="number" min="0" step="1" value={tier.price}
                          onChange={(e) => setDraftPrice(a.key, "price", e.target.value, i)}
                          className="w-20 rounded-lg bg-sand-50 border border-sand-200 px-2 py-1 font-mono outline-none focus:border-ink" />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
